/** @shell standard @header bar */
import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { appTheme, useThemeColors } from '../shared/theme';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import { useTranslation } from '../shared/i18n';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useAuthStore } from '../shared/auth/store';
import { useSubscriptionContext } from '../data/domains/subscription';
import { useAddresses } from '../data/domains/addresses';
import { useQueryClient } from '@tanstack/react-query';
import { runInvalidationForMutation } from '../data/query/runInvalidation';
import { DeliveryScheduleCore } from './components/DeliveryScheduleCore';
import { StackScreenShell, STACK_SECTION_GAP } from '../shared/theme/studio/stackScreenShell';

export const DeliveryScheduleScreen: React.FC = () => {
  const route = useRoute();
  const navigation = useAuthNavigation();
  const on_back = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const user = useAuthStore(s => s.user);
  const query_client = useQueryClient();
  const { activeSubscriptions, availableDates, getAddressDates } = useSubscriptionContext();
  const { data: addressesData, refetch: refetchAddresses } = useAddresses();

  const params = route.params as { address?: { id: number; name?: string } } | undefined;
  const address = params?.address;
  const addresses = useMemo(() => (Array.isArray(addressesData) ? addressesData : []), [addressesData]);

  const preset_dates = useMemo(
    () => (address?.id != null ? getAddressDates(address.id) : []),
    [address?.id, getAddressDates],
  );

  const handleSaved = useCallback(() => {
    void (async () => {
      if (typeof user?.id === 'number') {
        await runInvalidationForMutation(query_client, {
          rule_id: 'address.deliverySchedule',
          customer_id: user.id,
        });
      }
      await refetchAddresses?.();
      navigation.goBack();
    })();
  }, [navigation, query_client, refetchAddresses, user?.id]);

  const content_top = androidAppHeaderContentTop(insets.top) + STACK_SECTION_GAP;
  const { scrollY, onScroll } = useScreenHeaderScroll();

  if (!address?.id || user?.id == null) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader title={t('address.deliveryScheduleTitle')} onBack={on_back} scrollY={scrollY} />
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={t('address.deliveryScheduleTitle')} onBack={on_back} scrollY={scrollY} />
      <View style={styles.body}>
        <DeliveryScheduleCore
          address={address}
          activeSubscriptions={activeSubscriptions}
          availableDates={availableDates}
          presetDates={preset_dates}
          customerId={user.id}
          addresses={addresses}
          onSaved={handleSaved}
          onBack={on_back}
          contentTopPadding={content_top}
          contentBottomPadding={insets.bottom}
          onScroll={onScroll}
        />
      </View>
    </StackScreenShell>
  );
};

const styles = StyleSheet.create({
  body: { flex: 1 },
});

export default DeliveryScheduleScreen;
